import { getData, getDataWithAuth, postData, putData } from "./ApiSettings";

const USER_KEY = 'ovlm_user';

export const checkUserExists = async (email) => {
    try {
        const response = await getData(`/users?filters[$and][0][email][$eq]=${email}`);

        // console.log(response);
        
        return response?.data?.length > 0;
    } catch (e) {
        console.error('Error checking user existence:', e);
        throw e; // Propagate error up so we can handle it in the UI
    }
};

export const createGuestAccount = async (data) => {
    try {
        // Check if user exists first
        const userExists = await checkUserExists(data.email);
        if (userExists) {
            // If user exists, fetch their data
            const response = await getData(`/users?filters[$and][0][email][$eq]=${data.email}&populate=*`);
            return { 
                success: true, 
                isExisting: true,
                user: response.data[0]
            };
        }

        // Extract profile data
        const { first_name, last_name, ...registrationData } = data;
        const profileData = {
            first_name,
            last_name,
            isGuest: true,
            confirmed: false
        };
        
        // Create basic user first
        const response = await postData('/auth/local/register', registrationData);
        
        if (!response.data || !response.data.user) {
            throw new Error('Guest account creation failed');
        }
        
        // Update user with guest status and profile data
        await updateUser(response.data.user.id, profileData);
        
        // Fetch the complete updated user data
        const updatedUserResponse = await getData(`/users/${response.data.user.id}?populate=*`);
        
        // Return full user data without logging in
        return { 
            success: true, 
            isExisting: false,
            user: updatedUserResponse.data
        };
    } catch (e) {
        console.error('Error in guest account creation:', e);
        return { success: false, error: e.message };
    }
};

export const convertGuestToRegistered = async (email) => {
    try {
        // Find the user
        const response = await getData(`/users?filters[$and][0][email][$eq]=${email}`);
        const user = response?.data?.[0];
        
        if (!user) {
            throw new Error('User not found');
        }

        // Update the user to remove guest status and confirm the account
        await updateUser(user.id, {
            isGuest: false,
            confirmed: true
        });

        return true;
    } catch (e) {
        console.error('Error converting guest account:', e);
        return false;
    }
};

export const createUser = async (data) => {
    try {
        // Check if user exists first
        const userExists = await checkUserExists(data.email);
        if (userExists) {
            return { exists: true };
        }

        // Extract profile data
        const { first_name, last_name, ...registrationData } = data;
        const profileData = {};
        
        // Only add fields if they exist
        if (first_name) profileData.first_name = first_name;
        if (last_name) profileData.last_name = last_name;
        
        // Create user first
        const response = await postData('/auth/local/register', registrationData);

        console.log('response: ', response.data.user);
        
        if (!response.data || !response.data.user) {
            throw new Error('User creation failed');
        }
        
        // If we have profile data, update the user
        if (Object.keys(profileData).length > 0) {
            const updateResult = await updateUser(response.data.user.id, profileData);
            if (!updateResult) {
                console.warn('Profile update failed for new user');
            }
        }
        
        return response.data;
    } catch (e) {
        console.error('Error in user creation:', e);
        return false;
    }
};

export const updateUser = async (userId, data) => {
    try{
        await putData(`/users/${userId}`, data);
        return true;
    }catch(e){
        console.error('Error in user update:', e);
        return false;
    }
};

export const saveAddress = async (data) => {
    try{
        const response = await postData('/user-adresses', data);
        // console.log(response);
        return true;
    }catch(e){
        console.error('Error saving address: ', e);
    }
};

export const updateUserAddress = async (id, data) => {
    try{
        const response = await putData(`/user-adresses/${id}`, data);
        return true;
    }catch(e){
        console.error('Error updating user address: ', e);
    }
};

// export const getUserAddress = async (id) => {
//     try{
//         const response = await getData('user-adresses/')
//     }catch(e){
//         console.error('Error getting user address: ', e);
//     }
// };

export const loginUser = async (data) => {
    localStorage.setItem(USER_KEY, JSON.stringify(data));
    // console.log(data);
};

export const credentialCheck = async (data) => {
    try{
        const response = await postData('/auth/local', data);
        // console.log(response.data);
        return response.data;
    }catch(e){
        console.error(e);
        return false;
    }
}

export const checkLoggedIn = async () => {
    try {
        const userData = localStorage.getItem(USER_KEY);
        if (!userData) {
            return false;
        }

        const { jwt } = JSON.parse(userData);
        if (!jwt) {
            localStorage.removeItem(USER_KEY);
            return false;
        }

        // Test the token against Strapi's API
        await getDataWithAuth('/users/me', jwt);
        return true;
        
    } catch (error) {
        // If the request fails, the token is invalid/expired
        localStorage.removeItem(USER_KEY);
        return false;
    }
};

export const getCurrentUserData = async () => {
    try{
        if(await checkLoggedIn()){
            const userData = localStorage.getItem(USER_KEY);
            const { jwt } = JSON.parse(userData);
            const data = await getDataWithAuth('/users/me?populate=*', jwt);
            return data.data;
        }else{
            return false;
        }
    }catch(e){
        console.error('Error fetching user data: ', e);
    }
};

export const getCurrentUserAddress = async (id) => {
    try{
        if(await checkLoggedIn()){
            const userData = localStorage.getItem(USER_KEY);
            const { jwt } = JSON.parse(userData);
            const data = await getDataWithAuth(`/user-adresses/${id}`, jwt);
            return data.data.data;
        }else{
            return false;
        }
    }catch(e){
        console.error('Error fetching user data: ', e);
    }
};

/**
 * Returns the JWT of the currently logged-in user, or null if none.
 */
export const getCurrentJwt = () => {
    try {
        const userData = localStorage.getItem(USER_KEY);
        if (!userData) return null;
        const { jwt } = JSON.parse(userData);
        return jwt || null;
    } catch {
        return null;
    }
};

/**
 * GET helper that uses the user's JWT when logged in, falls back to the default
 * API token otherwise. Pass it for endpoints whose behavior differs based on
 * the authenticated user (e.g. product-sales hides vente-privée for anonymous).
 */
export const getDataAsCurrentUser = async (endpoint) => {
    const jwt = getCurrentJwt();
    if (jwt) return getDataWithAuth(endpoint, jwt);
    return getData(endpoint);
};

const REFERENCED_FLAG_KEY = 'ovlm_user_referenced';

/**
 * Snapshot lu de manière synchrone par SaleCartManager pour appliquer le
 * tarif vente privée. Rafraîchi à chaque appel de `checkIsReferenced()`.
 */
export const getReferencedFlag = () => {
    try {
        return localStorage.getItem(REFERENCED_FLAG_KEY) === '1';
    } catch {
        return false;
    }
};

/**
 * Returns true if the current logged-in user has made at least one purchase
 * on the platform (any flow). Used to grant access to "tarif vente privée".
 * Also caches the result in localStorage so synchronous consumers (cart
 * manager) can read it without re-hitting the API.
 */
export const checkIsReferenced = async () => {
    try {
        const userData = localStorage.getItem(USER_KEY);
        if (!userData) {
            localStorage.setItem(REFERENCED_FLAG_KEY, '0');
            return false;
        }
        const { jwt } = JSON.parse(userData);
        if (!jwt) {
            localStorage.setItem(REFERENCED_FLAG_KEY, '0');
            return false;
        }
        const response = await getDataWithAuth('/sale-orders/me/is-referenced', jwt);
        const referenced = Boolean(response?.data?.referenced);
        localStorage.setItem(REFERENCED_FLAG_KEY, referenced ? '1' : '0');
        return referenced;
    } catch (e) {
        console.error('Error checking referenced status:', e);
        return false;
    }
};

export const logout = async () => {
    try{
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(REFERENCED_FLAG_KEY);
        return true;
    }catch(e){
        console.error(e);
        return false;
    }
}